import "./style.scss";

import Input from "shared/ui/Input";
import { FC, useContext, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Field, Form, Formik, FormikProps } from "formik";
import SelectCountry from "shared/ui/SelectCountry";
import { createTrustfull } from "features/trustfull";
import { UserContext } from "features/account";
import Alert from "shared/ui/Alert";
import { decryptApiBodyToJson } from "shared/api/cryptApiBody";
import { TrustfullResponse } from "shared/types/types";

type TrustfullVerificationFormProps = {
  disabled: boolean;
  onVerificationComplete: (data: TrustfullResponse) => void;
  onVerificationError?: (error: string) => void;
  onSubmitTrustfull: (submitFn: () => void) => void;
  resetForm?: boolean;
  setLoading?: (loading: boolean) => void;
  hideBirthFields?: boolean;
};

export interface TrustfullFormValues {
  nom: string;
  prenom: string;
  email: string;
  tel: string;
  lieuNaissance: string;
  dateNaissance: string;
  documentCountry: string;
}

const TrustfullVerificationForm: FC<TrustfullVerificationFormProps> = ({
                                                                         disabled,
                                                                         onVerificationComplete,
                                                                         onVerificationError,
                                                                         onSubmitTrustfull,
                                                                         resetForm = false,
                                                                         setLoading,
                                                                         hideBirthFields = false
                                                                       }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { user } = useContext(UserContext);
  const type = user?.type ?? '';
  const formikRef = useRef<FormikProps<TrustfullFormValues>>(null);

  // Validation schema - TOUS les champs sont requis
  const validationSchema = Yup.object({
    nom: Yup.string()
      .required("Le nom est requis.")
      .min(2, "Le nom doit contenir au moins 2 caractères.")
      .max(20, "Le nom ne peut pas dépasser 20 caractères.")
      .matches(
        /^[A-Za-zÀ-ÖØ-öø-ÿ\s\-\\']+$/,
        "Seules les lettres, espaces, tirets, apostrophes et accents sont autorisés."
      ),
    prenom: Yup.string()
      .required("Le prénom est requis.")
      .min(2, "Le prénom doit contenir au moins 2 caractères.")
      .max(50, "Le prénom ne peut pas dépasser 50 caractères.")
      .matches(
        /^[A-Za-zÀ-ÖØ-öø-ÿ\s\-\\']+$/,
        "Seules les lettres, espaces, tirets, apostrophes et accents sont autorisés."
      ),
    email: Yup.string()
      .required("L'adresse e-mail est requise.")
      .email("L'adresse e-mail est invalide"),
    tel: Yup.string()
      .required("Le numéro de téléphone est requis.")
      .matches(/^[0-9]+$/, "Le numéro de téléphone doit contenir uniquement des chiffres.")
      .min(8, "Le numéro de téléphone doit contenir au moins 8 chiffres.")
      .max(15, "Le numéro de téléphone ne peut pas dépasser 15 chiffres."),
    lieuNaissance: hideBirthFields
      ? Yup.string()
      : Yup.string()
        .required("Le lieu de naissance est requis.")
        .min(2, "Le lieu de naissance doit contenir au moins 2 caractères.")
        .max(50, "Le lieu de naissance ne peut pas dépasser 50 caractères."),
    dateNaissance: hideBirthFields
      ? Yup.string()
      : Yup.string()
        .required("La date de naissance est requise.")
        .test(
          "not-in-future",
          "La date de naissance ne peut pas être dans le futur.",
          (value) => !value || new Date(value) <= new Date()
        ),
    documentCountry: Yup.string()
      .required("L'indicatif du pays est requis."),
  });

  const handleSubmit = async (values: TrustfullFormValues) => {
    console.log("Trustfull handleSubmit called with values:", values);

    try {
      setIsLoading(true);
      if (setLoading) setLoading(true);
      setErrorMessage("");

      const fullPhoneNumber = values.documentCountry + values.tel;
      console.log("Full phone number:", fullPhoneNumber);

      const response = await createTrustfull(
        fullPhoneNumber,
        values.email,
        values.prenom,
        values.nom,
        values.lieuNaissance,
        values.dateNaissance,
        type
      );

      console.log("Trustfull response:", response);

      if (response && response.data && response.status === 200) {
        const responseDecrypted = decryptApiBodyToJson(response.data);
        console.log("Decrypted response:", responseDecrypted);
        onVerificationComplete(responseDecrypted);
      } else {
        const errorMsg = "Une erreur s'est produite lors de la vérification des informations.";
        setErrorMessage(errorMsg);
        if (onVerificationError) onVerificationError(errorMsg);
      }
    } catch (error: any) {
      console.error("Erreur lors de la vérification Trustfull:", error);

      let errorMsg = "Une erreur inattendue s'est produite.";
      if (error.response) {
        if (error.response.status === 400) {
          errorMsg = "Données invalides. Veuillez vérifier les informations saisies.";
        } else if (error.response.status === 401) {
          errorMsg = "Erreur d'authentification. Veuillez vous reconnecter.";
        } else if (error.response.status === 404) {
          errorMsg = "Service de vérification temporairement indisponible.";
        } else if (error.response.status === 500) {
          errorMsg = "Erreur serveur. Veuillez réessayer plus tard.";
        } else {
          errorMsg = `Erreur ${error.response.status}: ${error.response.data?.message || 'Une erreur s\'est produite'}`;
        }
      } else if (error.request) {
        errorMsg = "Erreur de connexion. Veuillez vérifier votre connexion internet.";
      }

      setErrorMessage(errorMsg);
      if (onVerificationError) onVerificationError(errorMsg);
    } finally {
      setIsLoading(false);
      if (setLoading) setLoading(false);
    }
  };

  // Reset form when resetForm prop changes to true
  useEffect(() => {
    if (resetForm && formikRef.current) {
      // formikRef.current.resetForm();
    }
  }, [resetForm]);

  // Register the submit function with parent
  useEffect(() => {
    if (onSubmitTrustfull) {
      const submitWrapper = () => {
        console.log("Submit wrapper called");
        console.log("formikRef.current:", formikRef.current);
        console.log("formikRef.current?.values:", formikRef.current?.values);

        // Tous les champs sont requis : Formik valide et n'envoie que si le formulaire est valide
        if (formikRef.current) {
          console.log("Calling handleSubmit");
          formikRef.current.handleSubmit();
        }
      };

      onSubmitTrustfull(submitWrapper);
    }
  }, [onSubmitTrustfull, onVerificationError]);

  return (
    <div className="TrustfullVerificationForm">
      <h3 className="">Vérifier une personne <span className={"text-muted-foreground text-xs"}> (Etat civil)</span></h3>
      <Formik
        innerRef={formikRef}
        initialValues={{
          nom: "",
          prenom: "",
          email: "",
          tel: "",
          lieuNaissance: "",
          dateNaissance: "",
          documentCountry: "",
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnMount={false}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {(formik) => (
          <Form>
            <div className="d-flex flex-column">
              {errorMessage && (
                <div className="mb-3">
                  <Alert type="error">
                    {errorMessage}
                  </Alert>
                </div>
              )}

              <Input
                placeholder="Nom"
                name="nom"
                value={formik.values.nom}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={disabled || isLoading}
                errorMessage={
                  formik.touched.nom && formik.errors.nom
                    ? formik.errors.nom
                    : ""
                }
                className="mb-2"
              />

              <Input
                placeholder="Prénom"
                name="prenom"
                value={formik.values.prenom}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={disabled || isLoading}
                errorMessage={
                  formik.touched.prenom && formik.errors.prenom
                    ? formik.errors.prenom
                    : ""
                }
                className="mb-2"
              />

              <Input
                placeholder="Email"
                name="email"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={disabled || isLoading}
                errorMessage={
                  formik.touched.email && formik.errors.email
                    ? formik.errors.email
                    : ""
                }
                className="mb-2"
              />

              {!hideBirthFields && (
                <Input
                  placeholder="Lieu de naissance"
                  name="lieuNaissance"
                  value={formik.values.lieuNaissance}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={disabled || isLoading}
                  errorMessage={
                    formik.touched.lieuNaissance && formik.errors.lieuNaissance
                      ? formik.errors.lieuNaissance
                      : ""
                  }
                  className="mb-2"
                />
              )}

              {!hideBirthFields && (
                <Input
                  placeholder="Date de naissance"
                  name="dateNaissance"
                  type="date"
                  value={formik.values.dateNaissance}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={disabled || isLoading}
                  errorMessage={
                    formik.touched.dateNaissance && formik.errors.dateNaissance
                      ? formik.errors.dateNaissance
                      : ""
                  }
                  className="mb-2"
                />
              )}

              <div className="d-flex gap-1 mb-2">
                <div style={{ width: "40%" }}>
                  <Field
                    name="documentCountry"
                    value={formik.values.documentCountry}
                    id="documentCountry"
                  >
                    {({ field, country }: any) => (
                      <SelectCountry
                        field={field}
                        country={country}
                        phone={true}
                      />
                    )}
                  </Field>
                  {formik.touched.documentCountry &&
                    formik.errors.documentCountry && (
                      <div className="text-danger small mt-1">
                        {formik.errors.documentCountry}
                      </div>
                    )}
                </div>
                <div style={{ width: "70%" }}>
                  <Input
                    placeholder="Téléphone"
                    name="tel"
                    type="tel"
                    value={formik.values.tel}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      formik.setFieldValue("tel", value);
                    }}
                    onBlur={formik.handleBlur}
                    disabled={disabled || isLoading}
                    errorMessage={
                      formik.touched.tel && formik.errors.tel
                        ? formik.errors.tel
                        : ""
                    }
                  />
                </div>
              </div>

              <small className="mt-1 text-italic">
                <span className={"text-md text-danger"}>*</span> Tous les champs sont requis pour la vérification d'une personne
              </small>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default TrustfullVerificationForm;